import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green, orange } from "@material-ui/core/colors";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  Grid,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
} from "@material-ui/icons";

import * as pharmaApi from "../../services/pharmaApi";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  dropzone: {
    border: "2px dashed #ccc",
    borderRadius: 8,
    padding: theme.spacing(4),
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: "#fafafa",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: "#f0f7ff",
    },
  },
  dropzoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: "#f0f7ff",
  },
  previewTable: {
    maxHeight: 300,
    overflow: "auto",
    marginTop: theme.spacing(2),
  },
  columnSelect: {
    minWidth: 150,
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  mappingContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  successChip: {
    backgroundColor: green[500],
    color: "#fff",
  },
  errorChip: {
    backgroundColor: theme.palette.error.main,
    color: "#fff",
  },
  warningChip: {
    backgroundColor: orange[500],
    color: "#fff",
  },
  resultBox: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    borderRadius: 8,
  },
  successBox: {
    backgroundColor: "#e8f5e9",
    border: "1px solid #4caf50",
  },
  errorBox: {
    backgroundColor: "#ffebee",
    border: "1px solid #f44336",
  },
  progressBox: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: "#f5f5f5",
    border: "1px solid #e0e0e0",
  },
  progressBar: {
    height: 20,
    borderRadius: 10,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  progressLabel: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  statsRow: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: "wrap",
  },
  controlButtons: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  estimatedTime: {
    textAlign: "center",
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },
}));

const PRODUCT_FIELDS = [
  { key: "ean", label: "Codigo EAN/GTIN", required: false },
  { key: "sku", label: "SKU", required: false },
  { key: "name", label: "Nome do Produto", required: true },
  { key: "description", label: "Descricao", required: false },
  { key: "price", label: "Preco de Venda", required: false },
  { key: "maxPrice", label: "Preco Original/Maximo", required: false },
  { key: "costPrice", label: "Preco de Custo", required: false },
  { key: "category", label: "Categoria", required: false },
  { key: "manufacturer", label: "Fabricante", required: false },
  { key: "activePrinciple", label: "Principio Ativo", required: false },
  { key: "presentation", label: "Apresentacao", required: false },
  { key: "quantity", label: "Quantidade em Estoque", required: false },
  { key: "minQuantity", label: "Estoque Minimo", required: false },
  { key: "ncm", label: "NCM", required: false },
  { key: "requiresPrescription", label: "Tipo de Receita", required: false },
  { key: "isActive", label: "Ativo", required: false },
];

const DELIMITERS = [
  { value: ";", label: "Ponto e Virgula (;)" },
  { value: ",", label: "Virgula (,)" },
  { value: "\t", label: "Tab" },
  { value: "|", label: "Pipe (|)" },
];

// Threshold for using async import (10K rows)
const ASYNC_IMPORT_THRESHOLD = 10000;

const ProductImportModal = ({ open, onClose, onSuccess }) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const pollingRef = useRef(null);

  // State
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [delimiter, setDelimiter] = useState(";");
  const [hasHeader, setHasHeader] = useState(true);
  const [encoding, setEncoding] = useState("utf-8");
  const [updateExisting, setUpdateExisting] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [importResult, setImportResult] = useState(null);

  // Async import state
  const [useAsyncImport, setUseAsyncImport] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);

  const steps = ["Selecionar Arquivo", "Mapear Colunas", useAsyncImport ? "Importando..." : "Resultado"];

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const resetState = () => {
    setActiveStep(0);
    setLoading(false);
    setFile(null);
    setDelimiter(";");
    setHasHeader(true);
    setEncoding("utf-8");
    setUpdateExisting(true);
    setPreviewData(null);
    setColumnMapping({});
    setImportResult(null);
    setUseAsyncImport(false);
    setJobId(null);
    setJobStatus(null);
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const handleClose = () => {
    // Don't close if import is in progress
    if (jobStatus?.status === "processing") {
      toast.warning("Importacao em andamento. Use Pausar ou Cancelar para interromper.");
      return;
    }
    resetState();
    onClose();
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handlePreview = async () => {
    if (!file) {
      toast.error("Selecione um arquivo primeiro");
      return;
    }

    setLoading(true);
    try {
      const fileContent = await readFileAsBase64(file);

      const response = await pharmaApi.previewProductImport({
        fileContent,
        delimiter,
        hasHeader,
        encoding,
      });

      setPreviewData(response);

      // Determine if we should use async import
      const shouldUseAsync = response.totalRows >= ASYNC_IMPORT_THRESHOLD;
      setUseAsyncImport(shouldUseAsync);

      if (shouldUseAsync) {
        toast.info(`Arquivo grande detectado (${response.totalRows.toLocaleString()} linhas). Sera usado importacao em lotes com acompanhamento de progresso.`);
      }

      // Auto-map columns based on header names
      if (response.columns && response.columns.length > 0) {
        const autoMapping = {};
        response.columns.forEach((col) => {
          const lowerCol = col.toLowerCase().trim();

          if (lowerCol.includes("ean") || lowerCol.includes("gtin") || lowerCol.includes("barcode") || lowerCol.includes("codigo")) {
            autoMapping.ean = col;
          } else if (lowerCol.includes("sku") || lowerCol.includes("cod_interno")) {
            autoMapping.sku = col;
          } else if (lowerCol.includes("nome") || lowerCol.includes("name") || lowerCol.includes("descri")) {
            if (!autoMapping.name) autoMapping.name = col;
          } else if (lowerCol.includes("preco") || lowerCol.includes("price") || lowerCol.includes("valor")) {
            if (!autoMapping.price) autoMapping.price = col;
          } else if (lowerCol.includes("categ")) {
            autoMapping.category = col;
          } else if (lowerCol.includes("fabric") || lowerCol.includes("manuf")) {
            autoMapping.manufacturer = col;
          } else if (lowerCol.includes("principio") || lowerCol.includes("ativo")) {
            autoMapping.activePrinciple = col;
          } else if (lowerCol.includes("estoque") || lowerCol.includes("qtd") || lowerCol.includes("quant")) {
            autoMapping.quantity = col;
          }
        });
        setColumnMapping(autoMapping);
      }

      setActiveStep(1);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (fieldKey, columnName) => {
    setColumnMapping((prev) => ({
      ...prev,
      [fieldKey]: columnName || undefined,
    }));
  };

  // Poll job status
  const pollJobStatus = useCallback(async (currentJobId) => {
    try {
      const status = await pharmaApi.getImportJobStatus(currentJobId);
      setJobStatus(status);

      // If completed or failed, stop polling
      if (status.status === "completed" || status.status === "failed") {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }

        if (status.status === "completed") {
          setImportResult({
            total: status.totalRows,
            created: status.created,
            updated: status.updated,
            failed: status.failed,
            errors: status.recentErrors || [],
          });

          if (status.created > 0 || status.updated > 0) {
            toast.success(`Importacao concluida! ${status.created} criados, ${status.updated} atualizados`);
            if (onSuccess) onSuccess();
          }
        } else if (status.status === "failed") {
          toast.error(`Importacao falhou: ${status.errorMessage || "Erro desconhecido"}`);
        }
      }
    } catch (error) {
      console.error("Error polling job status:", error);
    }
  }, [onSuccess]);

  const startPolling = useCallback((currentJobId) => {
    // Poll every 1.5 seconds
    pollingRef.current = setInterval(() => {
      pollJobStatus(currentJobId);
    }, 1500);

    // Also poll immediately
    pollJobStatus(currentJobId);
  }, [pollJobStatus]);

  const handleImport = async () => {
    if (!columnMapping.name) {
      toast.error("O campo 'Nome do Produto' e obrigatorio");
      return;
    }

    setLoading(true);
    try {
      const fileContent = await readFileAsBase64(file);

      if (useAsyncImport) {
        // Start async import job
        const response = await pharmaApi.startImportJob({
          fileContent,
          columnMapping,
          options: {
            delimiter,
            hasHeader,
            encoding,
            updateExisting,
          },
          batchSize: 100,
        });

        setJobId(response.jobId);
        setJobStatus({
          jobId: response.jobId,
          status: "processing",
          totalRows: response.totalRows,
          processedRows: 0,
          created: 0,
          updated: 0,
          failed: 0,
          progress: 0,
        });

        toast.info(`Importacao iniciada! ${response.totalRows.toLocaleString()} linhas serao processadas.`);

        // Move to progress step and start polling
        setActiveStep(2);
        startPolling(response.jobId);
      } else {
        // Use synchronous import for small files
        const response = await pharmaApi.importProducts({
          fileContent,
          columnMapping,
          options: {
            delimiter,
            hasHeader,
            encoding,
            updateExisting,
          },
        });

        setImportResult(response);
        setActiveStep(2);

        if (response.created > 0 || response.updated > 0) {
          toast.success(`Importacao concluida! ${response.created} criados, ${response.updated} atualizados`);
          if (onSuccess) onSuccess();
        }
      }
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePauseJob = async () => {
    if (!jobId) return;
    try {
      await pharmaApi.pauseImportJob(jobId);
      toast.info("Importacao pausada");
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      // Update local status
      setJobStatus((prev) => ({ ...prev, status: "paused" }));
    } catch (error) {
      toastError(error);
    }
  };

  const handleResumeJob = async () => {
    if (!jobId) return;
    try {
      await pharmaApi.resumeImportJob(jobId);
      toast.info("Importacao retomada");
      setJobStatus((prev) => ({ ...prev, status: "processing" }));
      startPolling(jobId);
    } catch (error) {
      toastError(error);
    }
  };

  const handleCancelJob = async () => {
    if (!jobId) return;
    try {
      await pharmaApi.cancelImportJob(jobId);
      toast.warning("Importacao cancelada");
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      setJobStatus((prev) => ({
        ...prev,
        status: "failed",
        errorMessage: "Cancelado pelo usuario",
      }));
    } catch (error) {
      toastError(error);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds < 0) return "--";
    if (seconds < 60) return `${Math.ceil(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.ceil(seconds % 60);
    if (minutes < 60) return `${minutes}m ${secs}s`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const renderStep0 = () => (
    <Box>
      <Box
        className={`${classes.dropzone} ${file ? classes.dropzoneActive : ""}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept=".csv,.txt,.tsv"
          onChange={handleFileSelect}
        />
        <CloudUploadIcon style={{ fontSize: 48, color: "#999", marginBottom: 8 }} />
        <Typography variant="h6" color="textSecondary">
          {file ? file.name : "Arraste um arquivo ou clique para selecionar"}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Formatos aceitos: CSV, TXT, TSV
        </Typography>
      </Box>

      <Grid container spacing={2} style={{ marginTop: 16 }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>Delimitador</InputLabel>
            <Select
              value={delimiter}
              onChange={(e) => setDelimiter(e.target.value)}
              label="Delimitador"
            >
              {DELIMITERS.map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>Encoding</InputLabel>
            <Select
              value={encoding}
              onChange={(e) => setEncoding(e.target.value)}
              label="Encoding"
            >
              <MenuItem value="utf-8">UTF-8</MenuItem>
              <MenuItem value="latin1">ISO-8859-1 (Latin1)</MenuItem>
              <MenuItem value="cp1252">Windows-1252</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hasHeader}
                onChange={(e) => setHasHeader(e.target.checked)}
                color="primary"
              />
            }
            label="Primeira linha e cabecalho"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderStep1 = () => (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Mapeamento de Colunas
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Associe as colunas do arquivo aos campos do produto. Campos marcados com * sao obrigatorios.
        {useAsyncImport && (
          <Chip
            label={`${previewData?.totalRows?.toLocaleString()} linhas - Importacao em lotes`}
            color="primary"
            size="small"
            style={{ marginLeft: 8 }}
          />
        )}
      </Typography>

      <Box className={classes.mappingContainer}>
        {PRODUCT_FIELDS.map((field) => (
          <FormControl
            key={field.key}
            className={classes.columnSelect}
            variant="outlined"
            size="small"
          >
            <InputLabel>
              {field.label} {field.required ? "*" : ""}
            </InputLabel>
            <Select
              value={columnMapping[field.key] || ""}
              onChange={(e) => handleMappingChange(field.key, e.target.value)}
              label={`${field.label} ${field.required ? "*" : ""}`}
            >
              <MenuItem value="">
                <em>Nao mapear</em>
              </MenuItem>
              {previewData?.columns?.map((col) => (
                <MenuItem key={col} value={col}>
                  {col}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={updateExisting}
            onChange={(e) => setUpdateExisting(e.target.checked)}
            color="primary"
          />
        }
        label="Atualizar produtos existentes (por EAN/SKU)"
        style={{ marginTop: 16 }}
      />

      {previewData?.preview && previewData.preview.length > 0 && (
        <>
          <Typography variant="subtitle2" style={{ marginTop: 24 }}>
            Pre-visualizacao ({previewData.totalRows?.toLocaleString()} linhas no arquivo)
          </Typography>
          <Paper className={classes.previewTable}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {previewData.columns.map((col) => (
                    <TableCell key={col}>{col}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewData.preview.slice(0, 5).map((row, idx) => (
                  <TableRow key={idx}>
                    {previewData.columns.map((col) => (
                      <TableCell key={col}>{row[col] || "-"}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );

  const renderProgressStep = () => {
    if (!jobStatus) return null;

    const { status, totalRows, processedRows, created, updated, failed, progress, estimatedRemainingSeconds, errorMessage, recentErrors } = jobStatus;
    const isProcessing = status === "processing";
    const isPaused = status === "paused";
    const isCompleted = status === "completed";
    const isFailed = status === "failed";

    return (
      <Box className={classes.progressBox}>
        <Typography variant="h6" align="center" gutterBottom>
          {isProcessing && "Importando produtos..."}
          {isPaused && "Importacao pausada"}
          {isCompleted && "Importacao concluida!"}
          {isFailed && "Importacao com falha"}
        </Typography>

        {/* Progress bar */}
        <LinearProgress
          variant="determinate"
          value={progress || 0}
          className={classes.progressBar}
          color={isFailed ? "secondary" : "primary"}
        />

        {/* Progress label */}
        <Box className={classes.progressLabel}>
          <Typography variant="body2" color="textSecondary">
            {processedRows?.toLocaleString()} / {totalRows?.toLocaleString()} linhas
          </Typography>
          <Typography variant="body1" style={{ fontWeight: 600 }}>
            {progress || 0}%
          </Typography>
        </Box>

        {/* Estimated time */}
        {isProcessing && estimatedRemainingSeconds > 0 && (
          <Typography className={classes.estimatedTime}>
            Tempo restante estimado: {formatTime(estimatedRemainingSeconds)}
          </Typography>
        )}

        {/* Stats */}
        <Box className={classes.statsRow}>
          <Chip
            label={`${created || 0} criados`}
            className={classes.successChip}
            size="small"
          />
          <Chip
            label={`${updated || 0} atualizados`}
            color="primary"
            size="small"
          />
          {failed > 0 && (
            <Chip
              label={`${failed} erros`}
              className={classes.errorChip}
              size="small"
            />
          )}
        </Box>

        {/* Control buttons */}
        {(isProcessing || isPaused) && (
          <Box className={classes.controlButtons}>
            {isProcessing && (
              <Tooltip title="Pausar importacao">
                <IconButton onClick={handlePauseJob} color="default">
                  <PauseIcon />
                </IconButton>
              </Tooltip>
            )}
            {isPaused && (
              <Tooltip title="Retomar importacao">
                <IconButton onClick={handleResumeJob} color="primary">
                  <PlayIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Cancelar importacao">
              <IconButton onClick={handleCancelJob} color="secondary">
                <CancelIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Error message */}
        {isFailed && errorMessage && (
          <Box mt={2}>
            <Typography variant="body2" color="error" align="center">
              {errorMessage}
            </Typography>
            {(isPaused || isFailed) && processedRows < totalRows && (
              <Box display="flex" justifyContent="center" mt={1}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={handleResumeJob}
                  size="small"
                >
                  Retomar de onde parou
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Recent errors */}
        {recentErrors && recentErrors.length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle2" color="error">
              Ultimos erros ({recentErrors.length}):
            </Typography>
            <Paper style={{ maxHeight: 150, overflow: "auto", marginTop: 8 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Linha</TableCell>
                    <TableCell>Erro</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentErrors.slice(-10).map((err, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{err.row}</TableCell>
                      <TableCell>{err.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}
      </Box>
    );
  };

  const renderStep2 = () => {
    // If using async import, show progress
    if (useAsyncImport && jobStatus) {
      return renderProgressStep();
    }

    // Sync import result
    return (
      <Box>
        {importResult && (
          <>
            <Box
              className={`${classes.resultBox} ${
                importResult.failed === 0 ? classes.successBox : classes.errorBox
              }`}
            >
              <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
                {importResult.failed === 0 ? (
                  <CheckCircleIcon style={{ fontSize: 48, color: green[500] }} />
                ) : (
                  <ErrorIcon style={{ fontSize: 48, color: "#f44336" }} />
                )}
              </Box>
              <Typography variant="h6" align="center" gutterBottom>
                Importacao Concluida
              </Typography>
              <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
                <Chip
                  label={`${importResult.total} processados`}
                  variant="outlined"
                />
                <Chip
                  label={`${importResult.created} criados`}
                  className={classes.successChip}
                />
                <Chip
                  label={`${importResult.updated} atualizados`}
                  color="primary"
                />
                {importResult.failed > 0 && (
                  <Chip
                    label={`${importResult.failed} erros`}
                    className={classes.errorChip}
                  />
                )}
              </Box>
            </Box>

            {importResult.errors && importResult.errors.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" color="error">
                  Erros encontrados:
                </Typography>
                <Paper style={{ maxHeight: 200, overflow: "auto", marginTop: 8 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Linha</TableCell>
                        <TableCell>Erro</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importResult.errors.map((err, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{err.row}</TableCell>
                          <TableCell>{err.message}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };

  const canClose = !jobStatus || jobStatus.status === "completed" || jobStatus.status === "failed";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableBackdropClick={!canClose || loading}
      disableEscapeKeyDown={!canClose || loading}
    >
      <DialogTitle>
        Importar Produtos
        {loading && <LinearProgress style={{ marginTop: 8 }} />}
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} style={{ marginBottom: 24 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && renderStep0()}
        {activeStep === 1 && renderStep1()}
        {activeStep === 2 && renderStep2()}
      </DialogContent>
      <DialogActions>
        {activeStep === 0 && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePreview}
              disabled={!file || loading}
            >
              {loading ? <CircularProgress size={24} /> : "Proximo"}
            </Button>
          </>
        )}
        {activeStep === 1 && (
          <>
            <Button onClick={() => setActiveStep(0)} disabled={loading}>
              Voltar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleImport}
              disabled={!columnMapping.name || loading}
            >
              {loading ? <CircularProgress size={24} /> : `Importar ${previewData?.totalRows?.toLocaleString() || ""} produtos`}
            </Button>
          </>
        )}
        {activeStep === 2 && canClose && (
          <Button variant="contained" color="primary" onClick={handleClose}>
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProductImportModal;
